import { imageNode, shapeNode, textNode } from '@/templates'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { nodeHistoryStore } from './nodeHistoryStore'
import {
  type LayoutNode,
  type RichTextRun,
  type TextOptions,
  bakeEffectiveStyleIntoOptionsUpdate,
  stringToRuns,
} from '@backend/lib/db/typeRoots/layoutNode'

const { addHistoryStep, getCurrentHistory } = nodeHistoryStore.getState()

/** Semantic slot a text node fills on a canvas. The first six mirror the copy
 *  fields a generated post supplies; the rest are author-placed elements. */
export type Role =
  | 'caption'
  | 'header'
  | 'subheader'
  | 'text'
  | 'cta'
  | 'hashtags'
  | 'static'
  | 'image'
  | 'logo'

type UpdateConfig = { history?: boolean }

type Store = {
  nodes: LayoutNode[]
  // Multi-selection support
  selectedNodeIndices: number[]
  selectedNodes: LayoutNode[]
  // Legacy single selection (derived from selectedNodeIndices for backwards compatibility)
  selectedNodeIndex: number
  selectedNode: LayoutNode | null

  setNodes: (nodes: LayoutNode[], updateConfig?: UpdateConfig) => void
  // Multi-selection methods
  setSelectedNodeIndices: (indices: number[]) => void
  addToSelection: (index: number) => void
  removeFromSelection: (index: number) => void
  toggleSelection: (index: number) => void
  clearSelection: () => void
  // Legacy single selection (now sets single-item array)
  setSelectedNodeIndex: (index: number) => void

  addNode: (
    input:
      | { type: 'image' | 'logo'; url: string }
      | { type: 'text'; role: Role; content: string }
      | { type: 'shape'; shape: 'rectangle' | 'circle' | 'triangle' },
    canvas: { width: number; height: number },
    config?: UpdateConfig,
  ) => void
  updateNode: (
    index: number,
    updates: NestedPartial<LayoutNode>,
    updateConfig?: UpdateConfig,
  ) => void
  updateSelectedNodes: (updates: NestedPartial<LayoutNode>, updateConfig?: UpdateConfig) => void
  duplicateNode: (selectedNodeIndex: number, updateConfig?: UpdateConfig) => void
  duplicateSelectedNodes: (updateConfig?: UpdateConfig) => void
  deleteNode: (index: number, updateConfig?: UpdateConfig) => void
  deleteSelectedNodes: (updateConfig?: UpdateConfig) => void

  // Grouping methods
  groupSelectedNodes: (updateConfig?: UpdateConfig) => void
  ungroupSelectedNodes: (updateConfig?: UpdateConfig) => void

  addHistoryStep: () => void
}

export const nodeStore = create<Store>((set, get) => ({
  nodes: [],
  selectedNodeIndices: [],
  selectedNodes: [],
  // Legacy single selection (derived)
  selectedNodeIndex: -1,
  selectedNode: null,

  setNodes: (nodes, config) => {
    if (!nodes || !Array.isArray(nodes)) {
      console.warn('setNodes called with invalid nodes parameter:', nodes)
      return
    }
    const normalized = nodes.map(normalizeTextNodeContent)
    set((state) => {
      // Filter out invalid indices after nodes change
      const validIndices = state.selectedNodeIndices.filter((i) => i >= 0 && i < normalized.length)
      const selectedNodes = validIndices.map((i) => normalized[i]!)
      return {
        nodes: normalized,
        selectedNodeIndices: validIndices,
        selectedNodes,
        selectedNodeIndex: validIndices.length > 0 ? validIndices[0]! : -1,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })
    runUpdateEffect(config, get)
  },

  setSelectedNodeIndices: (indices) => {
    set(() => {
      const nodes = get().nodes
      const validIndices = indices.filter((i) => i >= 0 && i < nodes.length)
      const selectedNodes = validIndices.map((i) => nodes[i]!)
      return {
        selectedNodeIndices: validIndices,
        selectedNodes,
        selectedNodeIndex: validIndices.length > 0 ? validIndices[0]! : -1,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })
  },

  addToSelection: (index) => {
    set((state) => {
      if (state.selectedNodeIndices.includes(index)) return state
      if (index < 0 || index >= state.nodes.length) return state
      const newIndices = [...state.selectedNodeIndices, index]
      const selectedNodes = newIndices.map((i) => state.nodes[i]!)
      return {
        selectedNodeIndices: newIndices,
        selectedNodes,
        selectedNodeIndex: newIndices[0]!,
        selectedNode: selectedNodes[0]!,
      }
    })
  },

  removeFromSelection: (index) => {
    set((state) => {
      const newIndices = state.selectedNodeIndices.filter((i) => i !== index)
      const selectedNodes = newIndices.map((i) => state.nodes[i]!)
      return {
        selectedNodeIndices: newIndices,
        selectedNodes,
        selectedNodeIndex: newIndices.length > 0 ? newIndices[0]! : -1,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })
  },

  toggleSelection: (index) => {
    const state = get()
    if (state.selectedNodeIndices.includes(index)) {
      get().removeFromSelection(index)
    } else {
      get().addToSelection(index)
    }
  },

  clearSelection: () => {
    set(() => ({
      selectedNodeIndices: [],
      selectedNodes: [],
      selectedNodeIndex: -1,
      selectedNode: null,
    }))
  },

  // Legacy single selection (now sets single-item array)
  setSelectedNodeIndex: (selectedNodeIndex) => {
    if (selectedNodeIndex === -1) {
      get().clearSelection()
    } else {
      get().setSelectedNodeIndices([selectedNodeIndex])
    }
  },

  addNode: (input, canvas, config) => {
    let newNode: LayoutNode

    switch (input.type) {
      case 'image':
      case 'logo':
        newNode = imageNode(input.type, input.url, canvas)
        break
      case 'text':
        newNode = textNode(input.role, input.content, canvas)
        break
      case 'shape':
        newNode = shapeNode(input.shape, canvas)
        break
    }

    set((state) => {
      const newNodes = [...state.nodes, newNode]
      const newSelectedIndex = newNodes.length - 1
      return {
        nodes: newNodes,
        selectedNodeIndices: [newSelectedIndex],
        selectedNodes: [newNode],
        selectedNodeIndex: newSelectedIndex,
        selectedNode: newNode,
      }
    })

    runUpdateEffect(config, get)
  },

  updateNode: (index, updates, config) => {
    set((state) => {
      const updatedNodes = state.nodes.map((node, i) =>
        i === index ? updateNodeHelper(node, updates) : node,
      )
      const selectedNodes = state.selectedNodeIndices.map((i) => updatedNodes[i]!)
      return {
        nodes: updatedNodes,
        selectedNodes,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })

    runUpdateEffect(config, get)
  },

  updateSelectedNodes: (updates, config) => {
    set((state) => {
      const updatedNodes = state.nodes.map((node, i) =>
        state.selectedNodeIndices.includes(i) ? updateNodeHelper(node, updates) : node,
      )
      const selectedNodes = state.selectedNodeIndices.map((i) => updatedNodes[i]!)
      return {
        nodes: updatedNodes,
        selectedNodes,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })

    runUpdateEffect(config, get)
  },

  duplicateNode: (selectedNodeIndex, config) => {
    set((state) => {
      const selectedNode = state.nodes[selectedNodeIndex]
      if (!selectedNode) return state
      const duplicatedNode = {
        ...selectedNode,
        x: selectedNode.x + 20,
        y: selectedNode.y + 20,
        groupId: undefined,
      }
      const updatedNodes = [...state.nodes, duplicatedNode]
      const newSelectedIndex = updatedNodes.length - 1
      return {
        nodes: updatedNodes,
        selectedNodeIndices: [newSelectedIndex],
        selectedNodes: [duplicatedNode],
        selectedNodeIndex: newSelectedIndex,
        selectedNode: duplicatedNode,
      }
    })

    runUpdateEffect(config, get)
  },

  duplicateSelectedNodes: (config) => {
    set((state) => {
      if (state.selectedNodeIndices.length === 0) return state
      const duplicatedNodes = state.selectedNodeIndices.map((i) => ({
        ...state.nodes[i]!,
        x: state.nodes[i]!.x + 20,
        y: state.nodes[i]!.y + 20,
        groupId: undefined,
      }))
      const updatedNodes = [...state.nodes, ...duplicatedNodes]
      const newIndices = duplicatedNodes.map((_, i) => state.nodes.length + i)
      return {
        nodes: updatedNodes,
        selectedNodeIndices: newIndices,
        selectedNodes: duplicatedNodes,
        selectedNodeIndex: newIndices[0]!,
        selectedNode: duplicatedNodes[0]!,
      }
    })

    runUpdateEffect(config, get)
  },

  deleteNode: (index, config) => {
    set((state) => {
      const newNodes = state.nodes.filter((_, i) => i !== index)
      // Adjust indices after deletion
      const newIndices = state.selectedNodeIndices
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i))
      const selectedNodes = newIndices.map((i) => newNodes[i]!)
      return {
        nodes: newNodes,
        selectedNodeIndices: newIndices,
        selectedNodes,
        selectedNodeIndex: newIndices.length > 0 ? newIndices[0]! : -1,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })

    runUpdateEffect(config, get)
  },

  deleteSelectedNodes: (config) => {
    set((state) => {
      if (state.selectedNodeIndices.length === 0) return state
      const indicesToDelete = new Set(state.selectedNodeIndices)
      const newNodes = state.nodes.filter((_, i) => !indicesToDelete.has(i))
      return {
        nodes: newNodes,
        selectedNodeIndices: [],
        selectedNodes: [],
        selectedNodeIndex: -1,
        selectedNode: null,
      }
    })

    runUpdateEffect(config, get)
  },

  groupSelectedNodes: (config) => {
    set((state) => {
      if (state.selectedNodeIndices.length < 2) return state
      const groupId = crypto.randomUUID()
      const updatedNodes = state.nodes.map((node, i) =>
        state.selectedNodeIndices.includes(i) ? { ...node, groupId } : node,
      )
      const selectedNodes = state.selectedNodeIndices.map((i) => updatedNodes[i]!)
      return {
        nodes: updatedNodes,
        selectedNodes,
        selectedNode: selectedNodes[0]!,
      }
    })

    runUpdateEffect(config, get)
  },

  ungroupSelectedNodes: (config) => {
    set((state) => {
      if (state.selectedNodeIndices.length === 0) return state
      const updatedNodes = state.nodes.map((node, i) =>
        state.selectedNodeIndices.includes(i) ? { ...node, groupId: undefined } : node,
      )
      const selectedNodes = state.selectedNodeIndices.map((i) => updatedNodes[i]!)
      return {
        nodes: updatedNodes,
        selectedNodes,
        selectedNode: selectedNodes.length > 0 ? selectedNodes[0]! : null,
      }
    })

    runUpdateEffect(config, get)
  },

  addHistoryStep: () => addHistoryStep(get().nodes),
}))

/** Coerce any text node whose `data.content` is still a flat string into the
 *  new single-run array shape. Defends against unmigrated rows reaching the
 *  store from `setNodes` (initial load, history restore, server fetch) so
 *  downstream callers can rely on `content` being an array. */
const normalizeTextNodeContent = (node: LayoutNode): LayoutNode => {
  if (node.data.type !== 'text') return node
  const content = node.data.content as unknown
  if (Array.isArray(content)) return node
  if (typeof content !== 'string') return node
  return {
    ...node,
    data: {
      ...node.data,
      content: stringToRuns(content, node.data.options),
    },
  }
}

const runUpdateEffect = (config: UpdateConfig | undefined, get: () => Store) => {
  if (!config) return
  if (config.history) addHistoryStep(get().nodes)
}

/** Text option keys whose change invalidates a captured per-line layout —
 *  these affect glyph metrics or wrapping geometry, so the captured
 *  positions are no longer correct. Color and stroke are NOT here: they
 *  affect paint, not layout, and are handled by propagating the change
 *  down to `content` runs (so the rewrap fast path keeps painting per-run
 *  styles correctly without dropping the captured positions). */
const LAYOUT_AFFECTING_TEXT_OPTIONS = new Set([
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'horizontalAlign',
  'verticalAlign',
])

/** The five run-style fields that can be changed at the whole-node level and
 *  must be mirrored down into `content` runs so subsequent rewraps don't
 *  re-introduce the old style. */
const RUN_STYLE_OPTION_KEYS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'fontColor',
] as const

const shouldInvalidatePreWrappedLines = (
  node: LayoutNode,
  updates: NestedPartial<LayoutNode>,
): boolean => {
  if (node.data.type !== 'text') return false
  // Caller supplied a fresh preWrappedLines in the same update — trust it.
  // The editor uses this on edit-commit: it rewraps from runs and writes the
  // new layout alongside the new content in one update.
  const optsUpdate = (updates.data as { options?: Record<string, unknown> } | undefined)?.options
  if (optsUpdate && 'preWrappedLines' in optsUpdate) return false
  // Width changes cause re-wrap; height changes don't (vertical bound only).
  if (updates.width != null && updates.width !== node.width) return true
  const dataUpdate = updates.data
  if (!dataUpdate) return false
  if ('content' in dataUpdate && dataUpdate.content !== undefined) return true
  if (optsUpdate) {
    for (const key of Object.keys(optsUpdate)) {
      if (LAYOUT_AFFECTING_TEXT_OPTIONS.has(key)) return true
    }
  }
  return false
}

const updateNodeHelper = (node: LayoutNode, updates: NestedPartial<LayoutNode>): LayoutNode => {
  // When a text update will invalidate the captured preWrappedLines, the
  // legacy render path takes over and reads styling from TextOptions only.
  // For autodesign-extracted nodes those option fields hold the host
  // element's defaults (e.g. Inter/16/black), not the per-run styling the
  // canvas was actually painting (e.g. serif/64/white). Freeze the
  // displayed effective style into options *before* applying the user's
  // partial update so changing one field (color, padding, …) doesn't make
  // the other style fields collapse to host defaults.
  const willInvalidate =
    node.data.type === 'text' &&
    node.data.options.preWrappedLines !== undefined &&
    shouldInvalidatePreWrappedLines(node, updates)
  const rawOptionsUpdate = (updates.data?.options ?? {}) as Partial<TextOptions>
  const optionsUpdate =
    willInvalidate && node.data.type === 'text'
      ? bakeEffectiveStyleIntoOptionsUpdate(node.data, rawOptionsUpdate)
      : rawOptionsUpdate

  const merged: LayoutNode = {
    ...node,
    ...updates,
    // @ts-expect-error NestedPartial<LayoutNode> can't be narrowed back to a
    // single LayoutNode['data'] discriminant; the spread is correct at runtime.
    data: {
      ...node.data,
      ...(updates.data ?? {}),
      options: {
        ...node.data.options,
        ...optionsUpdate,
      },
    },
  }
  if (merged.data.type === 'text' && merged.data.options.preWrappedLines && willInvalidate) {
    merged.data = {
      ...merged.data,
      options: { ...merged.data.options, preWrappedLines: undefined },
    }
  }

  // Whole-node style changes must propagate to content runs. Otherwise the
  // toolbar update only writes to options, and any subsequent rewrap (which
  // reads content runs as the source of truth) re-introduces the old style.
  //
  // Two important details:
  //
  // 1. We propagate from `rawOptionsUpdate` (user-touched fields only), not
  //    the post-bake `optionsUpdate`. The bake widens to all five effective
  //    fields when preWrappedLines is invalidated — but those baked values
  //    represent "what was being painted" derived from existing content
  //    runs. Propagating them back to content runs would FLATTEN multi-style
  //    spans (e.g. a whole-node font change would force every run's color
  //    to the effective color, losing per-run colors). Only the user's
  //    actual touched fields should propagate.
  //
  // 2. Skip when the caller passed `data.content` in the same update —
  //    the content runs they sent are the new source of truth (e.g. the
  //    Lexical editor's commit), and overwriting them with options-derived
  //    scalars would flatten the multi-style content the user just typed.
  const updatesDataContent = (updates.data as { content?: unknown } | undefined)?.content
  if (merged.data.type === 'text' && updatesDataContent === undefined) {
    const styleDelta: Partial<RichTextRun> = {}
    for (const key of RUN_STYLE_OPTION_KEYS) {
      if (rawOptionsUpdate[key] !== undefined) {
        // @ts-expect-error narrowing across the run-style fields union
        styleDelta[key] = rawOptionsUpdate[key]
      }
    }
    if (Object.keys(styleDelta).length > 0) {
      const newContent = merged.data.content.map((run) => {
        let changed = false
        const out: RichTextRun = { ...run }
        for (const key of RUN_STYLE_OPTION_KEYS) {
          if (styleDelta[key] !== undefined && !valueEq(run[key], styleDelta[key])) {
            // @ts-expect-error narrowing across the run-style fields union
            out[key] = styleDelta[key]
            changed = true
          }
        }
        return changed ? out : run
      })
      merged.data = { ...merged.data, content: newContent }
    }
  }

  return merged
}

const valueEq = (a: unknown, b: unknown): boolean => {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  for (const k of Object.keys(ao)) if (ao[k] !== bo[k]) return false
  for (const k of Object.keys(bo)) if (ao[k] !== bo[k]) return false
  return true
}

nodeHistoryStore.subscribe((state, prev) => {
  if (state.historyCursor !== prev.historyCursor && state.historyCursor >= 0) {
    const history = getCurrentHistory()
    if (Array.isArray(history)) {
      nodeStore.getState().setNodes(history)
    }
  }
})

// Helper to get all nodes in the same group as the given node
export const getGroupMembers = (nodes: LayoutNode[], nodeIndex: number): number[] => {
  const node = nodes[nodeIndex]
  if (!node?.groupId) return [nodeIndex]
  return nodes.map((n, i) => (n.groupId === node.groupId ? i : -1)).filter((i) => i !== -1)
}

export const useNodeStore = () => {
  return nodeStore(useShallow((s) => s))
}
